import { Node, mergeAttributes, nodePasteRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embedUrl: {
      insertEmbedUrl: (attrs: { url: string }) => ReturnType;
      updateEmbedUrl: (attrs: Partial<EmbedUrlAttrs>) => ReturnType;
    };
  }
}

export interface EmbedUrlAttrs {
  url: string;
  title: string | null;
  description: string | null;
  favicon: string | null;
  provider: string | null;
  // User-supplied display label that overrides the fetched/metadata title
  // when set. `title` keeps the original/fetched value so refresh-metadata
  // can update it without trampling the user's customization.
  customTitle: string | null;
  // User-supplied description shown under the title on the embed button.
  // Mirrors `customTitle` — overrides fetched description when set and
  // survives metadata refresh.
  customDescription: string | null;
}

export interface EmbedUrlOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

export const EmbedUrl = Node.create<EmbedUrlOptions>({
  name: 'embedUrl',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      url: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-url') ?? '',
        renderHTML: (attrs) => ({ 'data-url': attrs.url ?? '' }),
      },
      title: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-title'),
        renderHTML: (attrs) =>
          attrs.title ? { 'data-title': attrs.title } : {},
      },
      description: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-description'),
        renderHTML: (attrs) =>
          attrs.description ? { 'data-description': attrs.description } : {},
      },
      favicon: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-favicon'),
        renderHTML: (attrs) =>
          attrs.favicon ? { 'data-favicon': attrs.favicon } : {},
      },
      provider: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-provider'),
        renderHTML: (attrs) =>
          attrs.provider ? { 'data-provider': attrs.provider } : {},
      },
      customTitle: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-custom-title'),
        renderHTML: (attrs) =>
          attrs.customTitle ? { 'data-custom-title': attrs.customTitle } : {},
      },
      customDescription: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-custom-description'),
        renderHTML: (attrs) =>
          attrs.customDescription
            ? { 'data-custom-description': attrs.customDescription }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="embed-url"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertEmbedUrl:
        (attrs) =>
        ({ commands }) => {
          const url = String(attrs?.url ?? '').trim();
          return commands.insertContent({
            type: this.name,
            attrs: {
              url,
              title: null,
              description: null,
              favicon: null,
              provider: url ? safeHost(url) : null,
            },
          });
        },
      updateEmbedUrl:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, attrs),
    };
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: /^(https?:\/\/[^\s<>"']+)\s*$/g,
        type: this.type,
        getAttributes: (match) => ({
          url: match[0].trim(),
          provider: safeHost(match[0].trim()),
        }),
      }),
    ];
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },
});

function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
